import { z } from 'zod';

export const PRE_STAGES = ['NEW', 'MEASURING', 'DESIGNING', 'COSTING', 'READY', 'QUOTED', 'WON', 'LOST'] as const;
export type PreStage = (typeof PRE_STAGES)[number];
export const PRE_STAGE_LABELS: Record<PreStage, string> = { NEW: 'New', MEASURING: 'Measuring', DESIGNING: 'Designing', COSTING: 'Costing', READY: 'Ready to quote', QUOTED: 'Quoted', WON: 'Won', LOST: 'Lost' };
export const PRE_SOURCES = { SHOWROOM: 'Showroom', PHONE: 'Phone', WHATSAPP: 'WhatsApp', WEBSITE: 'Website', REFERRAL: 'Referral', ARCHITECT: 'Architect / designer', OTHER: 'Other' } as const;
export const DESIGN_STAGES = ['NONE', 'BRIEF', 'DRAWING', 'FEASIBILITY', 'CUSTOMER', 'FROZEN'] as const;
export type DesignStage = (typeof DESIGN_STAGES)[number];
export const DESIGN_STAGE_LABELS: Record<DesignStage, string> = { NONE: 'Catalogue', BRIEF: 'Brief', DRAWING: 'Drawing', FEASIBILITY: 'Feasibility', CUSTOMER: 'With customer', FROZEN: 'Frozen' };
export type RevisionStatus = 'DRAFT' | 'FEASIBILITY' | 'RETURNED' | 'WITH_CUSTOMER' | 'CHANGES_REQUESTED' | 'APPROVED' | 'SUPERSEDED';

const uuid = z.string().uuid();
const optUuid = uuid.nullable().default(null);
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const preLineSchema = z
  .object({
    id: uuid.optional(),
    source: z.enum(['CATALOGUE', 'CUSTOM']),
    catalogueItemId: optUuid,
    productId: optUuid,
    itemId: optUuid,
    standardDescription: z.string().trim().max(1000).default(''),
    customerDescription: z.string().trim().max(2000).default(''),
    qty: z.coerce.number().positive('Above 0'),
    /** Inquiry Studio: custom furniture needs a drawing; standard / catalogue lines skip drawings. */
    drawingNeeded: z.boolean().optional(),
    sizes: z.string().trim().max(300).default(''),
  })
  .refine((l) => l.source === 'CUSTOM' || !!l.catalogueItemId || !!l.itemId, { path: ['catalogueItemId'], message: 'Pick from the catalogue' })
  .refine((l) => l.source === 'CATALOGUE' || l.customerDescription.length >= 3, { path: ['customerDescription'], message: 'Describe what the customer wants' });
export type PreLineInput = z.infer<typeof preLineSchema>;

export const preInquirySchema = z
  .object({
    docDate: day,
    customerId: optUuid,
    prospectName: z.string().trim().max(150).default(''),
    phone: z.string().trim().max(40).default(''),
    salespersonId: optUuid,
    siteAddress: z.string().trim().max(500).default(''),
    requestedDate: day.nullable().default(null),
    source: z.enum(Object.keys(PRE_SOURCES) as [keyof typeof PRE_SOURCES]).default('SHOWROOM'),
    notes: z.string().trim().max(2000).default(''),
    lines: z.array(preLineSchema).min(1, 'Add at least one line').max(50),
  })
  .refine((v) => !!v.customerId || v.prospectName.length >= 2, { path: ['prospectName'], message: 'Pick a customer or enter the prospect’s name' });
export type PreInquiryInput = z.infer<typeof preInquirySchema>;

export const preBriefSchema = z.object({
  widthCm: z.coerce.number().min(0).max(100000).nullable().default(null),
  depthCm: z.coerce.number().min(0).max(100000).nullable().default(null),
  heightCm: z.coerce.number().min(0).max(100000).nullable().default(null),
  material: z.string().trim().max(200).default(''),
  finish: z.string().trim().max(200).default(''),
  colour: z.string().trim().max(200).default(''),
  fabric: z.string().trim().max(200).default(''),
  notes: z.string().trim().max(3000).default(''),
});
export type PreBrief = z.infer<typeof preBriefSchema>;

export const preMeasurementSchema = z.object({
  plannedAt: z.string().min(16),
  notes: z.string().trim().max(1000).default(''),
});
export const preMeasuredSchema = z.object({
  notes: z.string().trim().max(2000).default(''),
  measurements: z.array(z.object({ label: z.string().trim().min(1).max(100), value: z.coerce.number(), unit: z.string().trim().max(10).default('cm') })).max(100),
});

export const preCostSheetSchema = z.object({
  materials: z.array(z.object({ itemId: optUuid, label: z.string().trim().max(200), qty: z.coerce.number().min(0), unitCost: z.coerce.number().min(0) })).max(100),
  operations: z.array(z.object({ operationTypeId: uuid, minutes: z.coerce.number().int().min(0).max(100000) })).max(30),
  packing: z.coerce.number().min(0).default(0),
  installation: z.coerce.number().min(0).default(0),
  transport: z.coerce.number().min(0).default(0),
  marginPct: z.coerce.number().min(0).max(90),
  /** A price set by hand overrides cost ÷ (1 − margin). */
  priceOverride: z.coerce.number().min(0).nullable().default(null),
});
export type PreCostSheetInput = z.infer<typeof preCostSheetSchema>;
export interface PreCostSheet extends PreCostSheetInput {
  materialCost: number;
  labourCost: number;
  overheadCost: number;
  totalCost: number;
  unitPrice: number;
  labourRate: number;
  overheadRate: number;
}

export interface PreRevisionItem { id: string; rev: number; status: RevisionStatus; designer: string | null; notes: string; feasibility: string | null; feasibilityNotes: string; feasibilityBy: string | null; customerFeedback: string; approvalEvidence: string; approvedAt: string | null; changeReason: string; createdAt: string; files: number }
export interface PreLineItem {
  id: string;
  lineNo: number;
  source: 'CATALOGUE' | 'CUSTOM';
  catalogueItem: { id: string; code: string; name: string } | null;
  product: { id: string; code: string; name: string; category: string } | null;
  item: { id: string; code: string; name: string } | null;
  standardDescription: string;
  customerDescription: string;
  qty: number;
  route: 'FAST' | 'DESIGN';
  designStage: DesignStage;
  brief: PreBrief;
  frozenRevisionId: string | null;
  frozenAt: string | null;
  revisions: PreRevisionItem[];
  costSheet: PreCostSheet | null;
  unitPrice: number | null;
}
export interface PreInquiryDetail {
  id: string;
  number: string | null;
  status: import('./documents').DocStatus;
  docDate: string;
  stage: PreStage;
  customer: { id: string; code: string; name: string } | null;
  prospectName: string;
  phone: string;
  salesperson: { id: string; name: string } | null;
  siteAddress: string;
  requestedDate: string | null;
  source: string;
  lostReason: string;
  notes: string;
  quotation: { id: string; number: string | null; status: string } | null;
  salesOrders: { id: string; number: string | null; status: string }[];
  hold: { until: string | null; start: string | null; finish: string | null; proposedDelivery: string | null } | null;
  lines: PreLineItem[];
  measurements: { id: string; plannedAt: string; doneAt: string | null; measuredBy: string | null; notes: string; measurements: { label: string; value: number; unit: string }[] }[];
  total: number | null;
  readyForQuote: { ready: boolean; missing: string[] };
  version: number;
}
export interface PreInquiryListItem { id: string; number: string | null; docDate: string; stage: PreStage; customer: string; salesperson: string | null; lines: number; designLines: number; frozen: number; total: number | null; requestedDate: string | null; ageDays: number }
export interface DesignBoardCard { lineId: string; inquiryId: string; number: string | null; customer: string | null; lineNo: number; description: string; product: string | null; stage: DesignStage; rev: number | null; revStatus: RevisionStatus | null; changeRequest: boolean; designer: string | null; waitingDays: number; requestedDate: string | null }

export interface ReleaseCheck {
  orderId: string;
  number: string | null;
  customer: string | null;
  total: number;
  enquiry: { id: string; number: string | null } | null;
  checks: { key: 'approved' | 'design' | 'advance' | 'slot'; label: string; ok: boolean; detail: string }[];
  advance: { required: number; paid: number; pct: number; override: string };
  slot: { start: string | null; finish: string | null; committed: string | null; confirmed: boolean };
  released: boolean;
  releasedAt: string | null;
}

// ---------- Inquiry Studio (2026-11-04) ----------

export const INQ_STAGES = ['INQUIRY', 'DRAWINGS', 'REVIEW', 'DISPATCH', 'COSTING', 'QUOTATION', 'ORDER'] as const;
export type InqStage = (typeof INQ_STAGES)[number];
export const INQ_STAGE_LABELS: Record<InqStage, string> = { INQUIRY: 'Inquiry', DRAWINGS: 'Drawings', REVIEW: 'Production review', DISPATCH: 'Dispatch date', COSTING: 'Pre-costing', QUOTATION: 'Quotation', ORDER: 'Sales order' };
export interface InqStageStatus { stage: InqStage; state: 'done' | 'active' | 'waiting' | 'blocked'; note: string; owners: string[]; progress?: { done: number; of: number } }

export type InqLineKind = 'FURNITURE' | 'MATTRESS' | 'BOUGHT_IN';
export interface InqLineOp { operationTypeId: string; code: string; name: string; colour: string; icon: string; seq: number; isMain: boolean; required: boolean; sectionId: string | null; section: string | null; entry: 'PER_PIECE' | 'TOTAL'; perPiece: number | null; minutes: number }
export interface InqLineMaterial { id: string; operationTypeId: string; item: { id: string; code: string; name: string; uom: string }; qty: number; enteredQty: number; unit: 'STOCK' | 'PURCHASE'; notes: string }
export interface InqLine {
  id: string;
  lineNo: number;
  kind: InqLineKind;
  item: { id: string; code: string; name: string } | null;
  description: string;
  qty: number;
  sizes: string;
  drawingNeeded: boolean;
  /** The latest drawing revision (attachments count) and the one approved in review. */
  drawing: { revisionId: string; rev: number; files: number; at: string } | null;
  review: { status: 'PENDING' | 'APPROVED' | 'RE_REVIEW' | 'NOT_NEEDED'; sections: { sectionId: string | null; section: string; approvedBy: string | null; at: string | null; owners: string[] }[] };
  cost: InqLineCost | null;
  plan: { routeId: string | null; route: string | null; planMode: 'SIMPLE' | 'DETAILED' | null; copiedFromJobId: string | null; operations: InqLineOp[]; materials: InqLineMaterial[]; totalMinutes: number };
}
export interface InqSuggestion { date: string | null; leadDays: number; sections: { sectionId: string | null; section: string; minutes: number; freePerDay: number; days: number }[] }
export interface InquiryStudio {
  inquiry: { id: string; number: string | null; status: string; version: number; customer: string | null; prospect: string; phone: string; salesperson: string | null; siteAddress: string; requestedDate: string | null; studioStage: string; stageSince: string };
  lines: InqLine[];
  stages: InqStageStatus[];
  suggestion: InqSuggestion;
  completion: { date: string | null; reason: string; by: string | null; at: string | null };
  dispatch: { date: string | null; installationDate: string | null; installationNeeded: boolean; notes: string; by: string | null; at: string | null; suggested: { dispatch: string | null; installation: string | null } };
  costing: { minMarginPct: number; materialCost: number; labourCost: number; overheadCost: number; extras: number; totalCost: number; sellingTotal: number; marginPct: number | null; belowMin: number[]; approval: { by: string | null; at: string | null; note: string }; costedAt: string | null; blockers: { lineNo: number; message: string; link?: { module: string; screen: string; id?: string } }[] };
  quotations: InqQuotation[];
  order: { id: string; number: string | null; status: string; productionStatus: string; at: string | null; by: string | null } | null;
  lost: { reason: string } | null;
  can: { edit: boolean; drawings: boolean; review: boolean; dispatch: boolean; cost: boolean; approveMargin: boolean; quote: boolean; accept: boolean; convert: boolean; sendToProduction: boolean };
}
export interface InquiryStudioRow { id: string; number: string | null; customer: string; requestedDate: string | null; lines: number; stage: string; stages: InqStageStatus[]; since: string; days: number; limit: number; overdue: boolean; owners: string[]; mine: boolean }

export const inqLinePlanSchema = z.object({
  routeId: uuid,
  planMode: z.enum(['SIMPLE', 'DETAILED']),
  operations: z.array(uuid).min(1).max(40),
  minutes: z.array(z.object({ operationTypeId: uuid, entry: z.enum(['PER_PIECE', 'TOTAL']), value: z.coerce.number().int().min(0).max(1_000_000).nullable() })).max(40),
  materials: z.array(z.object({ operationTypeId: uuid, itemId: uuid, qty: z.coerce.number().positive('Above 0').max(1e7), unit: z.enum(['STOCK', 'PURCHASE']).default('STOCK'), notes: z.string().trim().max(300).default('') })).max(200).default([]),
});
export type InqLinePlanInput = z.infer<typeof inqLinePlanSchema>;
export const inqReviewSchema = z.object({ sectionId: uuid.nullable(), notes: z.string().trim().max(500).default('') });
export interface InqCostRow { label: string; itemId?: string | null; sectionId?: string | null; qty: number; rate: number; amount: number; source: string }
/** Pre-costing of one line (whole line, all its quantity). */
export interface InqLineCost {
  material: InqCostRow[];
  labour: InqCostRow[];
  overhead: InqCostRow[];
  materialCost: number;
  labourCost: number;
  overheadCost: number;
  packing: number;
  delivery: number;
  installation: number;
  totalCost: number;
  unitPrice: number;
  sellingTotal: number;
  marginPct: number;
  warnings: string[];
  at?: string;
}
export const inqCostSchema = z.object({
  packing: z.coerce.number().min(0).max(10_000_000).default(0),
  delivery: z.coerce.number().min(0).max(10_000_000).default(0),
  installation: z.coerce.number().min(0).max(10_000_000).default(0),
  unitPrice: z.coerce.number().min(0).max(100_000_000),
});
export type InqCostInput = z.infer<typeof inqCostSchema>;
export const inqDispatchSchema = z.object({ dispatchDate: day, installationDate: day.nullable().default(null), notes: z.string().trim().max(500).default('') });
export const inqMarginApprovalSchema = z.object({ note: z.string().trim().min(5, 'Say why the low margin is accepted').max(500) });
export interface InqQuoteLine { lineId: string; lineNo: number; description: string; item: string | null; sizes: string; qty: number; unitPrice: number; amount: number; drawing: { revisionId: string; rev: number } | null }
export interface InqQuotation {
  id: string;
  rev: number;
  number: string;
  docDate: string;
  validUntil: string;
  /** OPEN, SUPERSEDED, ACCEPTED; EXPIRED = open past its date; OUTDATED = open but the costing changed after it. */
  state: 'OPEN' | 'SUPERSEDED' | 'ACCEPTED' | 'EXPIRED' | 'OUTDATED';
  lines: InqQuoteLine[];
  subtotal: number;
  vatPct: number;
  vat: number;
  total: number;
  terms: string;
  notes: string;
  by: string | null;
  at: string;
  accepted: { by: string | null; at: string | null; note: string };
  files: number;
}
/** Everything the quotation print needs: the quotation, who it is for and the approved drawings. */
export interface InqQuotePrint { quotation: InqQuotation; inquiry: InquiryStudio['inquiry']; drawings: { lineNo: number; rev: number; files: { id: string; fileName: string; contentType: string }[] }[] }
export const inqQuoteSchema = z.object({ validUntil: day.nullable().default(null), terms: z.string().trim().max(3000).default(''), notes: z.string().trim().max(1000).default('') });
export type InqQuoteInput = z.infer<typeof inqQuoteSchema>;
export const inqAcceptSchema = z.object({ note: z.string().trim().max(500).default('') });
/** Convert to a sales order. A prospect becomes a customer here (or an existing customer is chosen). */
export const inqConvertSchema = z.object({
  existingCustomerId: uuid.nullable().default(null),
  customer: z
    .object({
      name: z.string().trim().min(2, 'Enter the name').max(160),
      phone: z.string().trim().max(30).default(''),
      address: z.string().trim().max(400).default(''),
      trn: z.string().trim().max(20).default(''),
      customerType: z.enum(['REGULAR', 'ONE_TIME']),
      vatScenario: z.enum(['MAINLAND_MAINLAND', 'MAINLAND_INDIVIDUAL', 'SERVICES_UAE', 'MAINLAND_DZ_EXPORT', 'EXPORT_OUTSIDE_UAE']),
      placeOfSupply: z.string().nullable().default(null),
      paymentTermId: uuid,
      salespersonId: uuid,
    })
    .nullable()
    .default(null),
  /** A customer with the same phone or name exists and the user still wants a new one. */
  notDuplicate: z.boolean().default(false),
  salesSource: z.enum(['WEBSITE', 'SOCIAL_MEDIA', 'OFFLINE']).default('OFFLINE'),
  customerPo: z.string().trim().max(60).default(''),
  /** Free capacity moved since the review and the dates no longer hold: convert anyway, keeping the confirmed dates. */
  keepDates: z.boolean().default(false),
  notes: z.string().trim().max(1000).default(''),
});
export type InqConvertInput = z.infer<typeof inqConvertSchema>;
export const INQ_LOST_REASONS = ['PRICE', 'TIME', 'COMPETITOR', 'DESIGN', 'NO_RESPONSE', 'BUDGET', 'OTHER'] as const;
export const INQ_LOST_LABELS: Record<(typeof INQ_LOST_REASONS)[number], string> = { PRICE: 'Price too high', TIME: 'Delivery time too long', COMPETITOR: 'Went to a competitor', DESIGN: 'Design not agreed', NO_RESPONSE: 'No response from the customer', BUDGET: 'Budget / postponed', OTHER: 'Other' };
export const inqLostSchema = z.object({ reason: z.enum(INQ_LOST_REASONS), note: z.string().trim().max(500).default('') }).refine((v) => v.reason !== 'OTHER' || v.note.length >= 3, { path: ['note'], message: 'Say why it was lost' });
/** Post-costing of a converted inquiry: the estimate (pre-costing snapshot) against the actual cost of its jobs. */
export interface InqPostCostLine { lineNo: number; description: string; kind: InqLineKind; qty: number; estimate: { material: number; labour: number; overhead: number; extras: number; total: number } | null; actual: { material: number; labour: number; overhead: number; total: number; final: boolean; jobs: number } | null; selling: number; estMargin: number | null; actMargin: number | null; note: string }
export interface InqPostCosting { lines: InqPostCostLine[]; totals: { estimate: number; actual: number; selling: number; estMargin: number | null; actMargin: number | null } }
export interface InqQuoteRow { id: string; inquiryId: string; number: string; rev: number; customer: string; docDate: string; validUntil: string; total: number; state: InqQuotation['state'] }
export const inqCompletionSchema = z.object({ date: day, reason: z.string().trim().max(500).default('') });
